import { platformById } from "@ondit/publishing";
import {
	Button,
	ChipModal,
	ChipModalBody,
	ChipModalFooter,
	ChipModalHeader,
	ChipSelect,
	Input,
	Label,
	ModalDescription,
	Popover,
	PopoverContent,
	PopoverTrigger,
	ResourceOptions,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { useAccess } from "~/core/access/index";
import { ApiError, api } from "~/core/api/index";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Hint } from "~/core/ui/page";
import { SearchSelect } from "~/core/ui/search-select";
import { Async } from "~/core/ui/states";
import { PlatformIdentity } from "~/modules/analytics/components/platform-identity";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { PublishingCalendar } from "~/modules/analytics/features/platform-publishing/calendar";
import { PublishingContentEditor } from "~/modules/analytics/features/platform-publishing/content-editor";
import { PlatformTaskHistory, useExtensionReceipts } from "~/modules/analytics/features/platform-publishing/history";
import { StudioPublishingTask } from "~/modules/analytics/features/platform-publishing/studio-task";
import { PublisherConnection } from "../features/platform-publishing/install";

export function DistributionPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "distribution"],
		queryFn: () => api.distribution(entityId),
		...read,
	});
	// Only the titles are read here, so only the language is asked for: the
	// same list the publishing screens hold.
	const pages = useQuery({
		queryKey: [...keys.entity(entityId), "pages", filters.locale],
		queryFn: () => api.pages(entityId, { locale: filters.locale }),
		...read,
	});
	const [params, setParams] = useSearchParams();
	const taskId = params.get("task");
	const tasks = useQuery({
		queryKey: [...keys.entity(entityId), "platform-tasks"],
		queryFn: () => api.platformTasks(entityId),
		enabled: !!taskId,
	});
	const studioTask = tasks.data?.tasks.find((task) => task.id === taskId && task.payload.source.content_id);

	const [page, setPage] = useState(params.get("page") ?? "");
	const [platform, setPlatform] = useState(params.get("platform") ?? "");
	const selectedPlatform = state.data?.platforms.find((p) => p.id === platform);
	useExtensionReceipts(entityId);
	const [tab, setTab] = useState(
		["calendar", "prepare", "history", "platforms"].includes(params.get("tab") ?? "")
			? params.get("tab")!
			: params.get("page")
				? "prepare"
				: "calendar",
	);
	const [finishing, setFinishing] = useState(false);
	const [editing, setEditing] = useState(!!params.get("page") && !!params.get("platform"));
	const [busy, setBusy] = useState(false);
	const [recommendations, setRecommendations] = useState<
		{ platform_id: string; reason: string; cited_answers: number }[]
	>([]);
	const source = useQuery({
		queryKey: [...keys.entity(entityId), "distribution-source", page],
		queryFn: () => api.distributionSource(entityId, page),
		enabled: page !== "",
		...read,
	});
	const [search, setSearch] = useState("");
	// The recommendations are for the article they were asked about, and no
	// other: picking a different one clears them rather than leaving five
	// platforms reasoned about a page that is no longer on screen.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset on the article, not on every render
	useEffect(() => setRecommendations([]), [entityId, page]);

	/*
	 * Only from the button. A recommendation is a model call, and one that fired
	 * from an effect on every article picked spent credits on articles the
	 * person was only scrolling past to reach the one they meant.
	 */
	async function recommend() {
		if (!page || busy) return;
		setBusy(true);
		try {
			const result = await api.recommendPlatforms(entityId, page);
			setRecommendations(result.recommendations);
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setBusy(false);
		}
	}

	return (
		<StageShell
			entityId={entityId}
			reading="distribution"
			filters={filters}
			setFilters={setFilters}
			lede={t("publishFlow.intro")}
		>
			{!(tab === "prepare" && editing && page && platform) && <PublisherConnection />}
			<Tabs
				value={tab}
				onValueChange={(value) => {
					setTab(value);
					setParams(
						(previous) => {
							const next = new URLSearchParams(previous);
							next.set("tab", value);
							return next;
						},
						{ replace: true },
					);
				}}
				className="analytics-tabs"
			>
				<TabsList aria-label={t("nav.distribution")}>
					{["calendar", "prepare", "history", "platforms"].map((id) => (
						<TabsTrigger key={id} value={id}>
							{t(`publishFlow.tab.${id}`)}
						</TabsTrigger>
					))}
				</TabsList>
				<TabsContent value={tab}>
					{tab === "calendar" ? (
						<Async state={state}>
							{(data) => (
								<Async state={pages}>
									{(articles) => (
										<PublishingCalendar
											entityId={entityId}
											posts={data.posts}
											platforms={data.platforms}
											pages={articles}
											onEdit={(page, platform, task) => {
												setParams({ tab: "prepare", page, platform, ...(task ? { task } : {}) });
												setPage(page);
												setPlatform(platform);
												setEditing(true);
												setFinishing(false);
												setTab("prepare");
											}}
										/>
									)}
								</Async>
							)}
						</Async>
					) : tab === "history" ? (
						<>
							<PlatformTaskHistory entityId={entityId} />
							<Async state={state}>
								{(data) =>
									data.posts.length > 0 && (
										<section className="stack">
											<h3>{t("publishFlow.manualRecords")}</h3>
											{data.posts.map((post) => (
												<p key={post.id}>
													{post.title} · {data.platforms.find((p) => p.id === post.platform_id)?.name} ·{" "}
													{post.published_url ? (
														<a href={post.published_url} target="_blank" rel="noreferrer">
															{t("publishFlow.openResult")}
														</a>
													) : (
														t("distribution.draft")
													)}
												</p>
											))}
										</section>
									)
								}
							</Async>
						</>
					) : tab === "platforms" ? (
						<Async state={state}>
							{(data) => (
								<div className="stack">
									<div className="distribution-library-tools">
										<ResourceOptions
											translate={t}
											className="analytics-table-tools"
											search={{ value: search, onChange: setSearch, placeholder: t("distribution.search") }}
										/>
										<Hint text={t("distribution.rulesHint")} />
										<AddPlatform entityId={entityId} />
									</div>

									<div className="table-scroll">
										<Table variant="resource">
											<TableHeader>
												<TableRow>
													{["platform", "format", "images", "video", "links", "conditions"].map((key) => (
														<TableHead key={key}>{t(`distribution.${key}`)}</TableHead>
													))}
												</TableRow>
											</TableHeader>
											<TableBody>
												{data.platforms
													.filter((p) => `${p.name} ${p.domain}`.toLowerCase().includes(search.toLowerCase()))
													.map((p) => (
														<TableRow key={p.id}>
															<TableCell>
																<a href={p.url} target="_blank" rel="noopener noreferrer">
																	<PlatformIdentity platform={p} />
																</a>
																<div className="hint">{p.domain}</div>
																<div>
																	{(p.publishing_methods ?? ["manual"])
																		.map((m) => t(`distribution.method.${m}`))
																		.join(" · ")}
																</div>
															</TableCell>
															<TableCell>{t(`distribution.${p.format}`)}</TableCell>
															{(["images", "video", "links"] as const).map((flag) => (
																<TableCell key={flag}>{t(`distribution.${p[flag]}`)}</TableCell>
															))}
															<TableCell className="platform-conditions">
																{p.notes && <p>{pick(locale, p.notes)}</p>}
																{(p.reviewed_at || p.verified_at) && (
																	<span className="hint">
																		{t("distribution.reviewed", { date: p.reviewed_at ?? p.verified_at ?? "" })}
																	</span>
																)}
																<Popover>
																	<PopoverTrigger asChild>
																		<Button variant="ghost" size="sm">
																			{t("distribution.sources")}
																			<span className="sr-only"> · {p.name}</span>
																		</Button>
																	</PopoverTrigger>
																	<PopoverContent className="platform-sources">
																		<strong>
																			{p.name} · {t("distribution.sources")}
																		</strong>
																		{(p.sources?.length ? p.sources : [p.rules_url]).map((url, index) => (
																			<a key={url} href={url} target="_blank" rel="noopener noreferrer">
																				{index + 1}. {new URL(url).hostname}
																			</a>
																		))}
																		{p.logo_source_url && (
																			<a href={p.logo_source_url} target="_blank" rel="noopener noreferrer">
																				{t("distribution.logoSource")}
																			</a>
																		)}
																	</PopoverContent>
																</Popover>
															</TableCell>
														</TableRow>
													))}
											</TableBody>
										</Table>
									</div>
								</div>
							)}
						</Async>
					) : studioTask ? (
						<StudioPublishingTask key={studioTask.id} entityId={entityId} task={studioTask} />
					) : taskId && tasks.isPending ? (
						<Async state={tasks}>{() => null}</Async>
					) : tasks.error && taskId ? (
						<Async state={tasks}>{() => null}</Async>
					) : (
						<div className="publisher-flow">
							<ol className="publisher-steps" aria-label={t("publishFlow.steps")}>
								<li aria-current={!editing ? "step" : undefined}>
									<span>1</span>
									{t("publishFlow.choose")}
								</li>
								<li aria-current={editing && !finishing ? "step" : undefined}>
									<span>2</span>
									{t("publishFlow.check")}
								</li>
								<li aria-current={finishing ? "step" : undefined}>
									<span>3</span>
									{t("publishFlow.publish")}
								</li>
							</ol>
							{editing && selectedPlatform ? (
								<>
									<div className="publisher-selection">
										<div>
											<strong>{pages.data?.find((p) => p.id === page)?.title}</strong>
											<p className="hint">{selectedPlatform.name}</p>
										</div>
										<Button
											variant="ghost"
											onClick={() => {
												setEditing(false);
												setFinishing(false);
											}}
										>
											{t("publishFlow.changeSelection")}
										</Button>
									</div>
									<Async state={source}>
										{(article) =>
											article && (
												<PublishingContentEditor
													key={`${entityId}:${article.id}:${article.revision}:${platform}`}
													source={article}
													onStageChange={setFinishing}
													platform={selectedPlatform}
													entityId={entityId}
													existing={state.data?.posts.find((p) => p.page_id === page && p.platform_id === platform)}
												/>
											)
										}
									</Async>
								</>
							) : (
								<section className="publisher-choose">
									<h3>{t("publishFlow.chooseHeading")}</h3>
									<div className="publisher-choices">
										<Async state={pages}>
											{(items) => (
												<div className="field">
													<Label>{t("publishFlow.article")}</Label>
													<SearchSelect
														value={page}
														onChange={setPage}
														options={items
															.filter((p) => p.has_live_version)
															.map((p) => ({ value: p.id, label: p.title }))}
														label={t("publishFlow.article")}
														placeholder={t("distribution.chooseArticle")}
														searchLabel={t("table.search")}
														emptyLabel={t("table.noMatch")}
													/>
													{!items.some((p) => p.has_live_version) && <p>{t("publishFlow.noArticle")}</p>}
												</div>
											)}
										</Async>
										<Async state={state}>
											{(data) => (
												<div className="field">
													<Label>{t("publishFlow.destination")}</Label>
													<SearchSelect
														value={platform}
														onChange={setPlatform}
														options={data.platforms.map((p) => ({ value: p.id, label: p.name, keywords: p.domain }))}
														label={t("publishFlow.destination")}
														placeholder={t("publishFlow.selectPlatform")}
														searchLabel={t("distribution.search")}
														emptyLabel={t("table.noMatch")}
													/>
												</div>
											)}
										</Async>
									</div>
									{selectedPlatform && (
										<p className="hint">
											{t(
												platformById(platform)?.methods.includes("api")
													? platformById(platform)?.methods.includes("browser")
														? "publishFlow.bothMethods"
														: "publishFlow.apiMethod"
													: platformById(platform)?.methods.includes("browser")
														? "publishFlow.browserMethod"
														: "publishFlow.manualMethod",
											)}
										</p>
									)}
									<div className="platform-actions">
										<Button
											disabled={!page || !selectedPlatform}
											onClick={() => {
												setParams({ tab: "prepare", page, platform }, { replace: true });
												setEditing(true);
											}}
										>
											{t("publishFlow.continue")}
										</Button>
										<Button variant="ghost" disabled={!page || busy || !editor} onClick={recommend}>
											{busy ? t("distribution.recommending") : t("publishFlow.recommend")}
										</Button>
										<Hint text={t("distribution.recommendHint")} />
									</div>
									{recommendations.length > 0 && (
										<div className="publisher-recommendations">
											{recommendations.map((r) => (
												<div key={r.platform_id}>
													<Button variant="ghost" onClick={() => setPlatform(r.platform_id)}>
														{state.data?.platforms.find((p) => p.id === r.platform_id)?.name}
													</Button>
													<p className="hint">{r.reason}</p>
												</div>
											))}
										</div>
									)}
								</section>
							)}
						</div>
					)}
				</TabsContent>
			</Tabs>
		</StageShell>
	);
}

function AddPlatform({ entityId }: { entityId: string }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [busy, setBusy] = useState(false);
	const queryClient = useQueryClient();
	const create = useMutation({
		mutationFn: (platform: Parameters<typeof api.addPublishingPlatform>[1]) =>
			api.addPublishingPlatform(entityId, platform),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});
	const [name, setName] = useState("");
	const [url, setUrl] = useState("");
	const [format, setFormat] = useState("article");
	const [flags, setFlags] = useState({ images: "check", video: "check", links: "check" });
	const [limit, setLimit] = useState("");
	const [open, setOpen] = useState(false);
	async function add(e: React.FormEvent) {
		e.preventDefault();
		setBusy(true);
		try {
			await create.mutateAsync({
				id: "",
				name,
				url,
				domain: "",
				region: "global",
				format,
				...flags,
				max_length: limit ? Number(limit) : null,
				rules_url: url,
				verified_at: null,
			});
			setName("");
			setUrl("");
			setOpen(false);
			toast.success(t("distribution.saved"));
		} catch (error) {
			toast.error(error instanceof ApiError ? error.localized(locale) : String(error));
		} finally {
			setBusy(false);
		}
	}
	return (
		<>
			<Button variant="default" disabled={!editor} onClick={() => setOpen(true)}>
				{t("distribution.add")}
			</Button>
			<ChipModal open={open} onOpenChange={setOpen} dismissDisabled={busy} srTitle={t("distribution.add")}>
				<ChipModalHeader onClose={() => setOpen(false)} closeAriaLabel={t("common.close")}>
					{t("distribution.add")}
				</ChipModalHeader>
				<ChipModalBody className="px-(--s-8)">
					<ModalDescription>{t("distribution.rulesHint")}</ModalDescription>
					<form id="add-publishing-platform" onSubmit={add}>
						<fieldset className="bare-fieldset stack" disabled={!editor || busy}>
							<label className="field" htmlFor="distribution-platform">
								{t("distribution.platform")}
								<Input
									id="distribution-platform"
									required
									maxLength={100}
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
							</label>
							<label className="field" htmlFor="platform-url">
								URL
								<Input id="platform-url" required type="url" value={url} onChange={(e) => setUrl(e.target.value)} />
							</label>
							<div className="field">
								{t("distribution.format")}
								<ChipSelect
									value={format}
									onChange={setFormat}
									disabled={!editor || busy}
									aria-label={t("distribution.format")}
									options={["article", "social", "video"].map((v) => ({ value: v, label: t(`distribution.${v}`) }))}
								/>
							</div>
							{(["images", "video", "links"] as const).map((flag) => (
								<div className="field" key={flag}>
									{t(`distribution.${flag}`)}
									<ChipSelect
										value={flags[flag]}
										onChange={(value) => setFlags({ ...flags, [flag]: value })}
										disabled={!editor || busy}
										aria-label={t(`distribution.${flag}`)}
										options={["yes", "no", "restricted", "check"].map((v) => ({
											value: v,
											label: t(`distribution.${v}`),
										}))}
									/>
								</div>
							))}
							<label className="field" htmlFor="distribution-limit">
								{t("distribution.limit")}
								<Input
									id="distribution-limit"
									type="number"
									min={100}
									max={100000}
									value={limit}
									onChange={(e) => setLimit(e.target.value)}
								/>
							</label>
						</fieldset>
					</form>
				</ChipModalBody>
				<ChipModalFooter
					cancelLabel={t("common.cancel")}
					onCancel={() => setOpen(false)}
					primaryAction={{
						label: t("distribution.add"),
						type: "submit",
						form: "add-publishing-platform",
						disabled: !editor || busy,
					}}
				/>
			</ChipModal>
		</>
	);
}
